import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, sitesApi } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, TrendingDown, Wallet, ArrowRight, Plus, Upload, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const PIE_COLORS = ['#0ea5e9', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1', '#14b8a6'];
const CATEGORY_LABELS = { material: 'Material', service: 'Service', partner_fund: 'Partner Fund', customer_fund: 'Customer Fund', other: 'Other' };

function formatAmount(n) {
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toFixed(0);
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    sitesApi.list().then(r => setSites(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    dashboardApi.get(siteId || undefined)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [siteId]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const categoryPieData = (data.categories || []).map(c => ({ name: CATEGORY_LABELS[c.category] || c.category, value: c.total }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Financial overview at a glance</p>
        </div>
        <Select value={siteId} onValueChange={v => setSiteId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[200px]" data-testid="dashboard-site-filter">
            <SelectValue placeholder="All Sites" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sites</SelectItem>
            {sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border p-6" data-testid="total-money-in-card">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Total Money In</div>
            <div className="p-2 bg-emerald-50 dark:bg-emerald-950/30 rounded">
              <TrendingUp className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
            </div>
          </div>
          <div className="mt-3 font-mono text-2xl font-bold tracking-tight text-emerald-700 dark:text-emerald-400" data-testid="total-money-in-amount">
            {'\u20B9'}{data.total_in.toLocaleString('en-IN')}
          </div>
        </div>
        <div className="bg-card border border-border p-6" data-testid="total-money-out-card">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Total Money Out</div>
            <div className="p-2 bg-red-50 dark:bg-red-950/30 rounded">
              <TrendingDown className="w-4 h-4 text-red-700 dark:text-red-400" />
            </div>
          </div>
          <div className="mt-3 font-mono text-2xl font-bold tracking-tight text-red-700 dark:text-red-400">
            {'\u20B9'}{data.total_out.toLocaleString('en-IN')}
          </div>
        </div>
        <div className="bg-card border border-border p-6" data-testid="balance-card">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Current Balance</div>
            <div className="p-2 bg-muted rounded">
              <Wallet className="w-4 h-4" />
            </div>
          </div>
          <div className={`mt-3 font-mono text-2xl font-bold tracking-tight ${data.balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
            {'\u20B9'}{data.balance.toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly Chart */}
        <div className="bg-card border border-border p-6" data-testid="monthly-chart">
          <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-4">Monthly Cash Flow</h3>
          {data.monthly_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.monthly_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={formatAmount} />
                <Tooltip formatter={(v) => [`\u20B9${v.toLocaleString('en-IN')}`, '']} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 12 }} />
                <Bar dataKey="income" name="Money In" fill="#059669" radius={[2, 2, 0, 0]} />
                <Bar dataKey="expense" name="Money Out" fill="#DC2626" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">No data yet</div>
          )}
        </div>

        {/* Category Pie */}
        <div className="bg-card border border-border p-6" data-testid="category-chart">
          <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-4">Expense by Category</h3>
          {categoryPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={categoryPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} strokeWidth={1}>
                  {categoryPieData.map((entry, i) => <Cell key={entry.name || i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `\u20B9${v.toLocaleString('en-IN')}`} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">No expenses yet</div>
          )}
        </div>
      </div>

      {/* Site Comparison + Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Site comparison */}
        <div className="lg:col-span-2 bg-card border border-border p-6" data-testid="site-comparison">
          <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-4">Site Comparison</h3>
          {data.site_comparison.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.site_comparison} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={formatAmount} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={100} />
                <Tooltip formatter={(v) => `\u20B9${v.toLocaleString('en-IN')}`} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 12 }} />
                <Bar dataKey="income" name="Money In" fill="#059669" radius={[0, 2, 2, 0]} />
                <Bar dataKey="expense" name="Money Out" fill="#DC2626" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">No sites yet</div>
          )}
        </div>

        {/* Quick Insights */}
        <div className="bg-card border border-border p-6 space-y-4" data-testid="quick-insights">
          <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground">Quick Insights</h3>
          {data.top_category && (
            <div className="p-3 bg-muted">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Top Expense Category</div>
              <div className="font-semibold mt-1 text-sm capitalize">{CATEGORY_LABELS[data.top_category] || data.top_category}</div>
            </div>
          )}
          {data.top_site && (
            <div className="p-3 bg-muted">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Highest Spending Site</div>
              <div className="font-semibold mt-1 text-sm">{data.top_site}</div>
            </div>
          )}
          {data.partner_summary.length > 0 && (
            <div className="p-3 bg-muted">
              <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Partner Contributions</div>
              {data.partner_summary.slice(0, 3).map((p) => (
                <div key={p.entity_id || p.name} className="flex justify-between text-sm py-1">
                  <span className="truncate">{p.name}</span>
                  <span className="font-mono text-xs">{'\u20B9'}{p.invested.toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          )}

          {/* Quick Actions */}
          <div className="space-y-2 pt-2">
            <h4 className="text-xs text-muted-foreground uppercase tracking-wider">Quick Actions</h4>
            <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={() => navigate('/entries?action=new')} data-testid="insight-add-entry">
              <Plus className="w-3.5 h-3.5" /> Add Entry
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={() => navigate('/entries?action=bulk')} data-testid="insight-bulk-upload">
              <Upload className="w-3.5 h-3.5" /> Bulk Upload
            </Button>
            <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={() => navigate('/ledger')} data-testid="insight-view-ledger">
              <BookOpen className="w-3.5 h-3.5" /> View Ledger
            </Button>
          </div>
        </div>
      </div>

      {/* Recent Entries */}
      <div className="bg-card border border-border p-6" data-testid="recent-entries">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-[0.15em] text-muted-foreground">Recent Entries</h3>
          <button onClick={() => navigate('/entries')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            View All <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {data.recent_entries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-entries-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Date</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Type</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Category</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Entity</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_entries.map(e => (
                  <tr key={e._id} className="border-b border-border last:border-0">
                    <td className="py-2.5 px-3">{e.date}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-xs font-medium px-2 py-0.5 ${e.entry_type === 'income' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400'}`}>
                        {e.entry_type === 'income' ? 'Money In' : 'Money Out'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 capitalize">{CATEGORY_LABELS[e.category] || e.category}</td>
                    <td className="py-2.5 px-3">{e.entity_name || '-'}</td>
                    <td className={`py-2.5 px-3 text-right font-mono font-medium ${e.entry_type === 'income' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                      {'\u20B9'}{e.amount.toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-muted-foreground text-sm py-8">No entries yet. Start by adding your first entry.</div>
        )}
      </div>
    </div>
  );
}
